import { ROLE_LABELS, USER_ROLES, type AdminUserDTO, type CreateUserBody, type UpdateUserResponse, type UserRole } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { RelativeTime } from '../../../components/crm';
import { Badge, Button, Card, CardHeader, Dialog, Field, Input, Select, Switch, useToast } from '../../../components/ui';
import { useAuth } from '../../../hooks/useAuth';
import { api, errorMessage, fieldErrors } from '../../../lib/api';
import { pluralize } from '../../../lib/format';

const EMPTY: CreateUserBody = { email: '', name: '', role: 'sales_rep', temporaryPassword: '' };

export function UsersSection() {
  const { user: me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['settings', 'users'], queryFn: ({ signal }) => api.get<{ items: AdminUserDTO[] }>('/api/users', { signal }) });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<CreateUserBody>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resetting, setResetting] = useState<AdminUserDTO | null>(null);
  const [password, setPassword] = useState('');

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  };
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const create = useMutation({
    mutationFn: () => api.post<AdminUserDTO>('/api/users', draft),
    onSuccess: async (created) => {
      toast.success(`${created.name} can now sign in with the temporary password`);
      setAdding(false);
      setDraft(EMPTY);
      setErrors({});
      await refresh();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      onError(error);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { role?: UserRole; isActive?: boolean } }) => api.patch<UpdateUserResponse>(`/api/users/${id}`, patch),
    onSuccess: async (result) => {
      toast.success(result.sessionsRevoked ? `${result.user.name} was signed out everywhere` : 'Saved');
      await refresh();
    },
    onError,
  });
  const reset = useMutation({
    mutationFn: () => api.post(`/api/users/${resetting!.id}/reset-password`, { temporaryPassword: password }),
    onSuccess: async () => {
      toast.success('Password reset — share it with them in person or by phone');
      setResetting(null);
      setPassword('');
      await refresh();
    },
    onError,
  });
  const unlock = useMutation({ mutationFn: (id: string) => api.post(`/api/users/${id}/unlock`), onSuccess: refresh, onError });

  return (
    <Card>
      <CardHeader
        title="Users"
        description="Changing someone's role or deactivating them signs them out immediately. Their leads stay assigned."
        actions={
          <Button size="sm" variant="primary" icon={UserPlus} onClick={() => setAdding(true)}>
            Add user
          </Button>
        }
      />
      <ul className="divide-y divide-slate-100">
        {(data?.items ?? []).map((user) => (
          <li key={user.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-[13px]">
            <div className="min-w-48">
              <p className={user.isActive ? 'font-medium text-slate-900' : 'font-medium text-slate-400 line-through'}>{user.name}</p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
            <Select size="sm" aria-label={`Role of ${user.name}`} value={user.role} onChange={(event) => update.mutate({ id: user.id, patch: { role: USER_ROLES.find((r) => r === event.target.value) } })}>
              {USER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
            <Switch size="sm" checked={user.isActive} disabled={user.id === me?.id} onChange={(next) => update.mutate({ id: user.id, patch: { isActive: next } })} label="Active" />
            {user.lockedUntil && (
              <Button size="sm" icon={Lock} onClick={() => unlock.mutate(user.id)}>
                Unlock
              </Button>
            )}
            <span className="ml-auto text-xs text-slate-500">
              {pluralize(user.ownedLeadCount, 'lead')} · last sign-in {user.lastLoginAt ? <RelativeTime value={user.lastLoginAt} /> : 'never'}
            </span>
            {user.id === me?.id && <Badge size="sm">You</Badge>}
            <Button size="sm" variant="ghost" icon={KeyRound} onClick={() => setResetting(user)}>
              Reset password
            </Button>
          </li>
        ))}
      </ul>

      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a user"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Add user
            </Button>
          </>
        }
      >
        <div className="grid gap-3">
          <Field label="Name" error={errors.name}>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field label="Email" error={errors.email}>
            <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </Field>
          <Field label="Role">
            <Select value={draft.role} onChange={(e) => setDraft({ ...draft, role: USER_ROLES.find((r) => r === e.target.value) ?? 'sales_rep' })}>
              {USER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Temporary password" hint="At least 10 characters. Share it privately; they can change it after signing in." error={errors.temporaryPassword}>
            <Input value={draft.temporaryPassword} onChange={(e) => setDraft({ ...draft, temporaryPassword: e.target.value })} />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={resetting !== null}
        onClose={() => setResetting(null)}
        title={`Reset ${resetting?.name ?? ''}'s password`}
        description="They will be signed out everywhere and any lockout is cleared."
        footer={
          <>
            <Button variant="ghost" onClick={() => setResetting(null)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={password.length < 10} loading={reset.isPending} onClick={() => reset.mutate()}>
              Reset password
            </Button>
          </>
        }
      >
        <Field label="New temporary password" hint="At least 10 characters">
          <Input value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </Dialog>
    </Card>
  );
}
